import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { OrgBulkCreatePositionComponent } from './org-bulk-create-position/org-bulk-create-position.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MaterialModule } from '../../../material.module';
import { startWith, Subject, takeUntil } from 'rxjs';
import { Organization } from '@newgenus/common';
import { NgClass, NgStyle } from '@angular/common';

@Component({
    selector: 'shared-create-org-position-dialog',
    template: `
        <h2 mat-dialog-title class="m-0">
            New org part

            <!-- Close button. -->
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="h-100-70 w-100">
            <mat-tab-group animationDuration="200ms" [(selectedIndex)]="selectedTabIndex">
                <!-- ═══ Tab 1: Single Position (original) ═══ -->
                <mat-tab label="Single Org Part">
                    <div class="tab-body">
                        <div class="view-container m-3 w-100">
                            <div class="view-header">
                                <span class="viewer-title-spacer">Create new Org Part</span>
                                <button
                                    mat-raised-button
                                    (click)="savePosition()"
                                    color="accent"
                                    class="float-right"
                                    [disabled]="form.invalid"
                                >
                                    <mat-icon>save</mat-icon>Save
                                </button>
                            </div>

                            <div class="view-content">
                                @if (ancestorBreadcrumb.length > 0) {
                                    <div class="position-breadcrumb">
                                        <mat-icon class="breadcrumb-icon">account_tree</mat-icon>
                                        @for (
                                            ancestor of ancestorBreadcrumb;
                                            track ancestor.partKey;
                                            let last = $last
                                        ) {
                                            <span class="breadcrumb-item">
                                                <span class="breadcrumb-type">{{ ancestor.partType }}</span>
                                                <span class="breadcrumb-name">{{ ancestor.name }}</span>
                                            </span>
                                            @if (!last) {
                                                <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
                                            }
                                        }
                                        <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
                                        <span class="breadcrumb-item breadcrumb-new">
                                            <span class="breadcrumb-type">{{
                                                form.get('partType')?.value || 'new'
                                            }}</span>
                                            <span class="breadcrumb-name">{{
                                                form.get('name')?.value || 'New Org Part'
                                            }}</span>
                                        </span>
                                    </div>
                                }

                                <form [formGroup]="form" class="w-100">
                                    <div class="flex-row flex-wrap">
                                        <mat-form-field appearance="outline">
                                            <mat-label>Org Part Name</mat-label>
                                            <input matInput type="text" formControlName="name" />
                                        </mat-form-field>
                                    </div>

                                    <!-- create a drop down options using the data.partTypes -->

                                    <div class="flex-row flex-wrap">
                                        <mat-form-field appearance="outline">
                                            <mat-label>Part Type</mat-label>
                                            <mat-select formControlName="partType">
                                                @for (item of allowedPartTypes; track item) {
                                                    <mat-option [value]="item">
                                                        {{ item }}
                                                    </mat-option>
                                                }
                                                @if (!allowedPartTypes || allowedPartTypes.length === 0) {
                                                    <mat-option disabled>No part types available</mat-option>
                                                }
                                            </mat-select>
                                        </mat-form-field>
                                    </div>

                                    <div class="flex-row flex-wrap"></div>

                                    <div class="flex-row flex-wrap">
                                        <div>
                                            @if (data.node && !data.node.children && !data.node.siblings) {
                                                <p>
                                                    This org part will be the first of its kind. It will inherit
                                                    permissions from it's children, and it's siblings will have their
                                                    own branches of inheritance separate to this org part. Usually,
                                                    there is only one CEO who will be the root of authority.
                                                </p>
                                            } @else if (data.node && data?.type === 'child') {
                                                <p>
                                                    This new org part will be the
                                                    {{ toNth(phantomPart.position) }} child of
                                                    <strong>{{ data?.node?.name }}</strong
                                                    >.
                                                </p>
                                            } @else if (data?.node?.name) {
                                                <p>
                                                    This new org part will be the
                                                    {{ toNth(phantomPart.position) }} sibling to
                                                    <strong>{{ data?.node?.name }}</strong
                                                    >.
                                                </p>
                                            }
                                            <p>Drag to change order.</p>
                                        </div>
                                        <div cdkDropList class="example-list" (cdkDropListDropped)="drop($event)">
                                            @for (part of orderOfAppearance; track part; let i = $index) {
                                                <div
                                                    class="example-box"
                                                    [ngClass]="{ 'new-part': part.data.partKey === 'phantom' }"
                                                    [ngStyle]="getPartStyles(part)"
                                                    cdkDrag
                                                >
                                                    <div class="example-custom-placeholder" *cdkDragPlaceholder></div>
                                                    <div class="example-handle" cdkDragHandle>
                                                        <svg width="24px" fill="currentColor" viewBox="0 0 24 24">
                                                            <path
                                                                d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z"
                                                            ></path>
                                                            <path d="M0 0h24v24H0z" fill="none"></path>
                                                        </svg>
                                                    </div>
                                                    {{ toNth(i) }}) {{ part.data?.name || '' }}
                                                    @if (part.data?.partType) {
                                                        <span
                                                            class="part-type-badge"
                                                            [attr.data-type]="part.data.partType"
                                                            >{{ part.data.partType }}</span
                                                        >
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </mat-tab>

                <!-- ═══ Tab 2: Bulk Builder ═══ -->
                <mat-tab label="Bulk Builder">
                    <div class="tab-body">
                        <shared-org-bulk-create-position
                            [partTypes]="data.partTypes"
                            [anchorPartName]="data.node?.name || data.node?.node?.data?.name || ''"
                            [anchorPartKey]="data.node?.node?.data?.partKey || data.node?.partKey || ''"
                            [organization]="data.organization"
                            [ancestorBreadcrumb]="ancestorBreadcrumb"
                            [insertionType]="data.type"
                            [defaultColour]="getDefaultColour()"
                            [existingSiblings]="data.type === 'child' ? data.children || [] : data.siblings || []"
                            [allOrgParts]="data.organization.orgParts || []"
                            (closed)="onBulkClosed($event)"
                        >
                        </shared-org-bulk-create-position>
                    </div>
                </mat-tab>
            </mat-tab-group>
        </mat-dialog-content>

        <mat-dialog-actions align="end" [style.display]="selectedTabIndex === 0 ? '' : 'none'">
            <button mat-button mat-dialog-close>Close</button>
            <button
                mat-stroked-button
                (click)="savePosition()"
                color="accent"
                class="float-right"
                [disabled]="form.invalid"
            >
                <mat-icon>save</mat-icon>Save
            </button>
        </mat-dialog-actions>
    `,
    imports: [MaterialModule, ReactiveFormsModule, NgClass, NgStyle, OrgBulkCreatePositionComponent],
    styleUrls: ['./org-position-dialog.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgCreateNewPositionDialogComponent implements OnInit, OnDestroy {
    // Allowed child types per parent — matches backend validatePartType()
    private static readonly ALLOWED_CHILDREN: Record<string, string[]> = {
        executive: ['executive', 'division', 'post'],
        division: ['department', 'post'],
        department: ['section', 'unit', 'post'],
        section: ['post', 'unit'],
        unit: ['post'],
        post: [],
    };

    public orderOfAppearance: any[] = [];
    public ancestorBreadcrumb: { partKey: string; name: string; partType: string }[] = [];
    public allowedPartTypes: string[] = [];
    public selectedTabIndex = 0;
    private _excludedChildren: any[] = [];

    // ── Bulk save state ──
    // (Save/monitoring logic is now in the child component)
    public phantomPart = {
        position: 0,
        data: {
            name: 'New Position',
            partKey: 'phantom',
        },
    };

    public form = new FormGroup({
        name: new FormControl<string>('New Position', [Validators.required]),
        key: new FormControl<string>(''),
        colour: new FormGroup({
            color: new FormControl<string>(''),
            background: new FormControl<string>(''),
            linkColor: new FormControl<string>(''),
        }),
        parentPartKey: new FormControl<string>(''),
        parentRef: new FormControl<string>(''),
        partKey: new FormControl<string>('phantom'),
        partType: new FormControl<string>(''),
        path: new FormControl<string>(''),
        depth: new FormControl<number>(-1),
        children: new FormArray<any>([]),
    });

    public dialogRef = inject(MatDialogRef) as MatDialogRef<any>;
    public data = inject(MAT_DIALOG_DATA) as {
        organization: Organization;
        type: 'sibling' | 'child';
        node: any;
        partTypes: any[];
        siblings: any[];
        children: any[];
        defaultChildType?: string;
        defaultColours?: {
            color: string;
            background: string;
            linkColor: string;
        };
    };

    public destroy$ = new Subject<void>();

    public ngOnInit(): void {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160 sweep — hosts the
        // tree builder like Edit Org Part); phone-scoped, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
        console.log('OrgCreateNewPositionDialog', this.data);

        // Filter partTypes based on parent context
        this.allowedPartTypes = this.computeAllowedPartTypes();

        if (this.data.node) {
            // Use default colours from siblings/children if provided, otherwise fall back to parent node
            const defaultColours = this.data.defaultColours || {
                color: this.data.node.color || '#ffffff',
                background: this.data.node.background || '#000000',
                linkColor: this.data.node.linkColor || '#000000',
            };

            // Update the form colour values.
            this.form.patchValue({
                colour: defaultColours,
            });
        }

        if (this.data.type === 'child') {
            console.log('🧸 DEBUG - Creating CHILD position');
            console.log('  - Parent node:', this.data.node.node.data);
            console.log('  - Parent path:', this.data.node.node.data.path);
            console.log('  - Parent partKey:', this.data.node.node.data.partKey);
            console.log('  - Parent partType:', this.data.node.node.data.partType);
            console.log('  - Children count:', this.data.children.length);

            let partType = '';
            const parentType = this.data.node.node.data.partType;

            // Use explicit defaultChildType if provided (e.g. from "Add Child Unit" or "Add Child Post" menus)
            if (this.data.defaultChildType) {
                partType = this.data.defaultChildType;
            } else if (parentType === 'executive') {
                partType = 'division'; // default; user can also pick 'post'
            } else if (parentType === 'division') {
                partType = 'department'; // default; user can also pick 'post'
            } else if (parentType === 'department') {
                partType = 'section';
            } else if (parentType === 'section') {
                partType = 'post';
            } else if (parentType === 'unit') {
                partType = 'post';
            }
            console.log('  - Calculated child partType:', partType);

            // `this.data.node.node.data.path` would be something like: 'children.X.children.X.data'
            // The assumed path would then be: 'children.X.children.X.children.Y.data'
            // Where Y is the position of the new child.
            let path = this.data.node.node?.data?.path || '';
            console.log('  - Original parent path:', path);

            if (path) {
                if (path.endsWith('.data')) {
                    path = path.slice(0, -5);
                }
                path += `.children.${this.data.children.length}.data`;
                console.log('  - Calculated child path:', path);
            } else {
                console.log('  - WARNING: No path found for parent');
                // TODO: Not sure...
            }

            this.orderOfAppearance = (this.data.children || []).sort(
                (a: any, b: any) => this.extractIndexFromPath(a.data?.path) - this.extractIndexFromPath(b.data?.path)
            );

            // When a specific child type was requested (e.g. "Add Child Post"),
            // only show same-type siblings in the reorder list.
            // Other-type children are stored separately and merged back on save.
            if (this.data.defaultChildType) {
                const targetType = this.data.defaultChildType;
                this._excludedChildren = this.orderOfAppearance.filter(
                    (child: any) => child.data?.partType !== targetType
                );
                this.orderOfAppearance = this.orderOfAppearance.filter(
                    (child: any) => child.data?.partType === targetType
                );
            }

            const formData = {
                parentPartKey: this.data.node?.node?.data?.partKey,
                parentRef: this.data.node.node?.data?.path,
                partType: partType,
                depth: (this.data.node?.node?.data?.depth || -1) + 1,
                path: path,
            };

            console.log('  - Final form data for child:', formData);
            this.form.patchValue(formData);
        } else if (this.data.type === 'sibling') {
            console.log('👯 DEBUG - Creating SIBLING position');
            console.log('  - Sibling node:', this.data.node);
            console.log('  - Sibling data:', this.data.node.node.data);
            console.log('  - Sibling path:', this.data.node.node.data.path);
            console.log('  - Sibling parentPartKey:', this.data.node.node.data.parentPartKey);
            console.log('  - Sibling parentRef:', this.data.node.node.data.parentRef);
            console.log('  - Siblings count:', this.data.siblings.length);

            const partType = this.data.node.node.data.partType;
            console.log('  - Sibling partType:', partType);

            // For siblings, we need to construct the base path from the current node's path
            // The path will be updated correctly when items are reordered
            let path = this.data.node.node?.data?.path || '';
            console.log('  - Original sibling path:', path);

            if (path) {
                if (path.endsWith('.data')) {
                    path = path.slice(0, -5); // Remove '.data'
                }
                // Remove the last index number (the current sibling's position)
                const lastDotIndex = path.lastIndexOf('.');
                if (lastDotIndex !== -1) {
                    path = path.substring(0, lastDotIndex);
                }
                // Add the new sibling position at the end, will be updated when dragged
                path += `.${this.data.siblings.length}.data`;
                console.log('  - Calculated sibling path:', path);
            } else {
                console.log('  - WARNING: No path found for sibling');
                // TODO: Not sure...
            }

            this.orderOfAppearance = (this.data.siblings || []).sort(
                (a: any, b: any) => this.extractIndexFromPath(a.data?.path) - this.extractIndexFromPath(b.data?.path)
            );

            const formData = {
                parentPartKey: this.data.node?.node?.data?.parentPartKey,
                parentRef: this.data.node.node?.data?.parentRef,
                partType: partType,
                depth: this.data.node.node?.data?.depth,
                path: path, // Set the initial path here
            };

            console.log('  - Final form data for sibling:', formData);
            this.form.patchValue(formData);
        }
        this.phantomPart.data = this.form.value as any;
        this.phantomPart.data.partKey = 'phantom';
        this.orderOfAppearance.push(this.phantomPart);

        // Build ancestor breadcrumb to show structural context
        this.buildAncestorBreadcrumb();

        this.form.controls['name'].valueChanges
            .pipe(startWith(this.form.controls['name'].value), takeUntil(this.destroy$))
            .subscribe((value) => {
                const idx = this.orderOfAppearance.findIndex((x) => x.data.partKey === 'phantom');
                if (~idx) {
                    this.orderOfAppearance[idx].data.name = value || '';
                }

                if (value) {
                    this.form.controls['key'].setValue(value.replace(/\s/g, '-').toLowerCase());
                } else {
                    this.form.controls['key'].setValue('');
                }
            });
    }

    public savePosition(): void {
        const indexOfPhantom = this.orderOfAppearance.findIndex((x) => x.data.partKey === 'phantom');
        if (~indexOfPhantom) {
            this.orderOfAppearance[indexOfPhantom].data = this.form.value;
        }

        // Merge excluded (other-type) children back before computing final positions.
        // Excluded items keep their original order, placed before the same-type items.
        if (this._excludedChildren.length > 0) {
            this.orderOfAppearance = [...this._excludedChildren, ...this.orderOfAppearance];
        }

        // Close the dialog.
        this.dialogRef.close({
            newPart: this.form.value,
            orderOfAppearance: this.updatePositionsAndPaths(),
        });
    }

    public drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.orderOfAppearance, event.previousIndex, event.currentIndex);

        setTimeout(() => {
            // Update the position of each entry.
            this.updatePositionsAndPaths();
        });
    }

    private updatePositionsAndPaths() {
        // console.log('🔄 DEBUG - updatePositionsAndPaths called');
        // console.log('  - Current orderOfAppearance:', this.orderOfAppearance.map(p => ({
        //     name: p.data.name,
        //     position: p.position,
        //     path: p.data.path,
        //     partKey: p.data.partKey
        // })));

        this.orderOfAppearance = this.orderOfAppearance.map((part, newIndex) => {
            // Update position.
            part.position = newIndex;
            // console.log(`  - Updating position ${newIndex} for ${part.data.name}`);

            // Update the paths.
            const lastDotIndex = part.data.path?.lastIndexOf('.');

            // console.log(`    - Original path: ${part.data.path}`);
            // console.log(`    - Last dot index: ${lastDotIndex}`);

            if (lastDotIndex !== -1 && lastDotIndex !== undefined) {
                const secondLastDot = part.data.path.lastIndexOf('.', lastDotIndex - 1);
                // console.log(`    - Second last dot index: ${secondLastDot}`);

                // Replace contents of the last square brackets with the new position.
                if (secondLastDot !== -1) {
                    const firstSection = part.data.path.slice(0, secondLastDot + 1);
                    const lastSection = part.data.path.slice(lastDotIndex);
                    const newPath = firstSection + newIndex + lastSection;

                    // console.log(`    - First section: ${firstSection}`);
                    // console.log(`    - Last section: ${lastSection}`);
                    // console.log(`    - New path: ${newPath}`);

                    part.data.path = newPath;

                    // Update the phantom part if this is the phantom part.
                    if (part.data.partKey === 'phantom') {
                        (this.phantomPart.data as any).path = newPath;
                        this.form.patchValue({ path: newPath });
                        // console.log(`    - Updated phantom part path to: ${newPath}`);
                    }
                }
            }

            return part;
        });

        // console.log('  - Final orderOfAppearance:', this.orderOfAppearance.map(p => ({
        //     name: p.data.name,
        //     position: p.position,
        //     path: p.data.path,
        //     partKey: p.data.partKey
        // })));

        return this.orderOfAppearance || [];
    }

    /**
     * Return the ordinal suffix of a number.
     * @param index the number to get the suffix for.
     */
    public toNth(index: number): string {
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const v = (index + 1) % 100;
        return index + 1 + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
    }

    /**
     * Get the styles for a part in the reorder list.
     * Returns background and text color based on the part's colour settings.
     */
    public getPartStyles(part: any): { [key: string]: string } {
        // For the phantom (new) part, use the default colours from the form
        if (part.data?.partKey === 'phantom') {
            const formColour = this.form.get('colour')?.value;
            if (formColour?.background && formColour?.color) {
                return {
                    'background-color': formColour.background,
                    color: formColour.color,
                };
            }
        }

        // For existing parts, use their stored colours
        const colour = part.data?.colour;
        if (colour?.background && colour?.color) {
            return {
                'background-color': colour.background,
                color: colour.color,
            };
        }

        // Default styling
        return {};
    }

    /**
    /**
     * Compute the allowed partTypes for the dropdown based on the parent's type.
     * For 'child' mode: allowed children of the clicked node.
     * For 'sibling' mode: allowed children of the clicked node's parent.
     * Falls back to the full list if parent context is unavailable.
     */
    private computeAllowedPartTypes(): string[] {
        const allTypes = this.data.partTypes || [];
        if (!this.data.node) return allTypes;

        let parentType: string | undefined;

        if (this.data.type === 'child') {
            // Adding a child: parent is the clicked node
            parentType = this.data.node.node?.data?.partType || this.data.node.partType;
        } else {
            // Adding a sibling: parent is the clicked node's parent
            const parentPartKey = this.data.node.node?.data?.parentPartKey || this.data.node.parentPartKey;
            if (parentPartKey) {
                const orgParts = this.data.organization?.orgParts || [];
                const parentPart = orgParts.find((p) => p.partKey === parentPartKey);
                parentType = parentPart ? (parentPart as any).partType : undefined;
            }
        }

        if (parentType && OrgCreateNewPositionDialogComponent.ALLOWED_CHILDREN[parentType]) {
            const allowed = OrgCreateNewPositionDialogComponent.ALLOWED_CHILDREN[parentType];
            // Return intersection with allTypes to respect any external filtering
            return allTypes.filter((t: string) => allowed.includes(t));
        }

        return allTypes;
    }

    /**
     * Extract the sibling index from a path string.
     * Path format: children.X.data or children.X.children.Y.data
     * E.g., "children.0.children.2.data" -> 2
     */
    private extractIndexFromPath(path: string): number {
        if (!path) return 0;

        const pathParts = path.split('.');

        // Work backwards from the end to find the last children.X pattern
        for (let i = pathParts.length - 1; i >= 0; i--) {
            if (pathParts[i] === 'data' && i > 0 && pathParts[i - 1].match(/^\d+$/)) {
                return parseInt(pathParts[i - 1], 10);
            }
        }

        return 0;
    }

    /**
     * Build ancestry breadcrumb by walking up parentPartKey from the reference node.
     * Shows the full structural context: e.g. Executive > Division > Department > [New Position]
     */
    private buildAncestorBreadcrumb(): void {
        const orgParts = this.data.organization?.orgParts || [];
        const partsMap = new Map(orgParts.map((p) => [p.partKey, p]));
        const ancestors: { partKey: string; name: string; partType: string }[] = [];

        // Determine the parent partKey based on whether this is a child or sibling
        let currentKey: string | undefined;
        if (this.data.type === 'child') {
            // For a child, the reference node IS the parent
            currentKey = this.data.node?.node?.data?.partKey;
        } else {
            // For a sibling, walk up to the shared parent
            currentKey = this.data.node?.node?.data?.parentPartKey;
        }

        // Walk up the ancestor chain
        const visited = new Set<string>();
        while (currentKey && !visited.has(currentKey)) {
            visited.add(currentKey);
            const part = partsMap.get(currentKey);
            if (part) {
                ancestors.unshift({
                    partKey: part.partKey,
                    name: part.name,
                    partType: part.partType || '',
                });
                currentKey = part.parentPartKey;
            } else {
                break;
            }
        }

        this.ancestorBreadcrumb = ancestors;
    }

    /**
     * Handle the close event from the Bulk Builder child component.
     * The child now handles save/monitoring internally.
     */
    public onBulkClosed(event: { saved: boolean }): void {
        this.dialogRef.close(event.saved ? { bulkSaved: true } : undefined);
    }

    /**
     * Get the default colour for the bulk builder, derived from the data context.
     */
    public getDefaultColour(): { color: string; background: string; linkColor: string } {
        const dc = this.data.defaultColours;
        if (dc) return { color: dc.color, background: dc.background, linkColor: dc.linkColor };

        const node = this.data.node;
        return {
            color: node?.color || node?.node?.data?.colour?.color || '#ffffff',
            background: node?.background || node?.node?.data?.colour?.background || '#000000',
            linkColor: node?.linkColor || node?.node?.data?.colour?.linkColor || '#000000',
        };
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
